/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited 2023.
 */

import { Attachment } from '@/components/blocks/Attachment';
import { LocalizationWithComponent } from '@/components/blocks/LocalizationWithComponent';
import { productAttrLabelSX, productAttrRowSX, productAttrValueSX, productCustomAttrLabelSX, productDetailsTabsDescriptionSX, showHideDescriptionSX } from '@/components/blocks/ProductDetails/styles/tabsDescription';
import { TabData } from '@/components/blocks/Tabs';
import { TYPES } from '@/data/constants/product';
import { useProductDetails } from '@/data/Content/ProductDetails';
import { useSkuListTable } from '@/data/Content/SkuListTable';
import { ContentContext } from '@/data/context/content';
import { useLocalization } from '@/data/Localization';
import {
	Attachment as AttachmentType,
	ProductAttribute,
	ProductAttributeValue,
	ResponseProductAttribute,
} from '@/data/types/Product';
import { getProductDisplayInfo } from '@/utils/getProductDisplayInfo';
import { productIsA } from '@/utils/productIsA';
import { Box, Button, Typography } from '@mui/material';
import HTMLReactParser from 'html-react-parser';
import { uniq, uniqWith } from 'lodash';
import { FC, Fragment, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSettings } from '@/data/Settings';
import { loadExternalScripts, removeScripts } from '@/utils/loadExternalScripts';
import { ItnlIntegrationScript } from '@/data/types/ItnlIntegrationScript';
import { PDPDescriptionScrollIntoView, showMoreLessDescriptionScript } from '@/utils/itnlIntegrationScriptsScripts';
import { useDeviceType } from '@/data/deviceDetection';
import { TabsMobile } from './TabsMobile';
import { TabsDesktop } from './TabsDesktop';
import { combineSX } from '@/utils/combineSX';
import { useSupplierData } from '@/data/Content/SuppplierData';
import { findAttributeByIdentifier } from '@/utils/productAttributes';
import { createCustomAttributes } from '@/utils/createCustomAttributes';

type Props = {
	attachments: AttachmentType[];
	longDescription: string;
	shortDescription: string;
	descriptiveAttributes: ProductAttribute[];
	longCompDesc: string[];
	customAttrObject?:  Record<string,any> | {};
};

const comparator = (l: ProductAttributeValue, r: ProductAttributeValue) => l.value === r.value;
const emptyAttrValues: ProductAttributeValue[] = [];

const LongDesc: FC<Props> = ({ longDescription, longCompDesc, shortDescription }) => {
	useEffect(()=>{
		const scripts:ItnlIntegrationScript[] = [
			{id: "pdp_description_scroll_into_view", scriptContent:PDPDescriptionScrollIntoView},
			{id: "pdp_description_lees_meer_action", scriptContent:showMoreLessDescriptionScript()}
		]
		loadExternalScripts(scripts);
		return(()=>removeScripts(scripts));
	},[]);
	const productDtlsNLS = useLocalization('productDetail');
	return (
		<>
			{longDescription && shortDescription &&
				<>
					<Typography sx={productDetailsTabsDescriptionSX} my={2} id="product_shortdescription">
						{HTMLReactParser(shortDescription)}
					</Typography>
					<Typography component="div" sx={productDetailsTabsDescriptionSX} id="product_longdescription" display='none'>
						{HTMLReactParser(longDescription)}
					</Typography>
					<Box>
						<Button 
							variant="textButton" 
							sx={showHideDescriptionSX} 
							id="show_more_description"
							aria-label={productDtlsNLS.ShowMoreARIA.t()}
						>
							{productDtlsNLS.ShowMore.t()}
						</Button>
						<Button 
							variant="textButton" 
							sx={combineSX([showHideDescriptionSX, {display:'none', mt:2.5}])} 
							id="show_less_description"
							aria-label={productDtlsNLS.ShowLessARIA.t()}
						>
							{productDtlsNLS.ShowLess.t()}
						</Button>
					</Box>
					{longCompDesc?.length ? (
						<ul>
							{uniq(longCompDesc).map((v, key) => (
								<li key={key}>{v}</li>
							))}
						</ul>
					) : null}
				</>
			}
		</>
	);
}

const DescriptiveAttributes: FC<Props> = ({ descriptiveAttributes , customAttrObject}) => {
	const {settings} = useSettings();
	const hideProductAttributes = settings.userData.HideProductAttributes;
	if(hideProductAttributes && hideProductAttributes.length>0){
		const hideAttrArr:string[] = hideProductAttributes.split(",");
		descriptiveAttributes = descriptiveAttributes.filter((attr:ResponseProductAttribute)=>!hideAttrArr.includes(attr.identifier));
	}
	const localization = useLocalization('productDetail');
	const descriptiveAttributesLess = descriptiveAttributes?.slice(0,5);
	const descriptiveAttributesMore = descriptiveAttributes?.slice(5);
	const productDtlsNLS = useLocalization('productDetail');
	const showHideSpecs = useCallback((action:string) => {
		const moreElem = document.querySelector<HTMLElement>("#descriptive_attributes_more");
		const descriptiveAttributesShowMore = document.querySelector<HTMLElement>("#descriptive_attributes_show_more");
		const descriptiveAttributesShowLess = document.querySelector<HTMLElement>("#descriptive_attributes_show_less");
		const customAttributeContainer = document.querySelector<HTMLElement>("#customAttributeContainer");
		if(moreElem){
			if(action==="hide"){
				moreElem.style.display = "none";
				const descriptiveAttributesShowMore = document.querySelector<HTMLElement>("#descriptive_attributes_show_more");
				if(descriptiveAttributesShowMore)descriptiveAttributesShowMore.style.display = "block";
				if(descriptiveAttributesShowLess)descriptiveAttributesShowLess.style.display = "none";
				if(customAttributeContainer)customAttributeContainer.style.display = "none";
			}else{
				moreElem.style.display = "block";
				if(descriptiveAttributesShowMore)descriptiveAttributesShowMore.style.display = "none";
				if(descriptiveAttributesShowLess)descriptiveAttributesShowLess.style.display = "block";
				if(customAttributeContainer)customAttributeContainer.style.display = "block";				
			}
		}
		
	},[]);
	return descriptiveAttributesLess?.length ? (
		<Box data-testid="product-details-container" id="product-details-container">
			{descriptiveAttributesLess.map(({ identifier, name, values = emptyAttrValues }) => (
				<Box
					key={`product-attribute-${identifier}`}
					data-testid={`product-attribute-${identifier}`}
					id={`product-attribute-${identifier}`}
					sx={productAttrRowSX}
				>
					<LocalizationWithComponent
						text={localization.descAttrLabelWithTagValues.t({ name })}
						components={[
							<Typography variant="body2" component="span" key="0" sx={productAttrLabelSX} />,
							<Fragment key="1">
								{uniqWith(values, comparator).map(({ identifier: vIdentifier, value }, _, all) => (
									<Typography sx={productAttrValueSX} key={vIdentifier} {...(all.length === 1 && { component: 'span' })}>
										 {Array.isArray(value) ? value.join(', ') : value}
									</Typography>
								))}
							</Fragment>,
						]}
					/>
				</Box>
			)
			)}
			<Box className="descriptive-attributes-more" id="descriptive_attributes_more" display="none">
				{descriptiveAttributesMore?.map(({ identifier, name, values = emptyAttrValues }) => (
					<Box
						key={`product-attribute-${identifier}`}
						data-testid={`product-attribute-${identifier}`}
						id={`product-attribute-${identifier}`}
						sx={productAttrRowSX}
					>
						<LocalizationWithComponent
							text={localization.descAttrLabelWithTagValues.t({ name })}
							components={[
								<Typography variant="body2" component="span" key="0" sx={productAttrLabelSX} />,
								<Fragment key="1">
									{uniqWith(values, comparator).map(({ identifier: vIdentifier, value }, _, all) => (
										<Typography key={vIdentifier} {...(all.length === 1 && { component: 'span' })}>
											{Array.isArray(value) ? value.join(', ') : value}
										</Typography>
									))}
								</Fragment>,
							]}
						/>
					</Box>
				))}
			</Box>
			<Box id="customAttributeContainer">{Object.keys(customAttrObject ?? {} as Record<string, any>)?.map((title, index) => {
				const rowData = (customAttrObject as Record<string, any>)[title];
				return (
					<>
						<Typography sx={productCustomAttrLabelSX} >{title}</Typography>
						 
						{Object.keys(rowData).map((key, index) =>

							<Box
								key={`product-attribute-${key}`}
								data-testid={`product-attribute-${key}`}
								id={`product-attribute-${key}`}
								sx={productAttrRowSX}
							>								
								<Typography variant="body2" component="span" sx={productAttrLabelSX}>
									{key}
								</Typography>
								<Typography sx={productAttrValueSX} component= "span">
									 {rowData[key]}
								</Typography>
							</Box>
						)}
					</>
				);
			})}
			</Box>
			<Box>
				<Button
					variant="textButton" 
					sx={combineSX([showHideDescriptionSX,{mt:2}])} 
					id="descriptive_attributes_show_more" 
					onClick={()=>showHideSpecs("show")}
					aria-label={productDtlsNLS.ShowMoreARIAProductSpecificaties.t()}
				>
						{productDtlsNLS.ShowMore.t()}
				</Button>
				<Button 
					variant="textButton" 
					sx={combineSX([showHideDescriptionSX,{mt:2.5, display:'none'}])} 
					id="descriptive_attributes_show_less" 
					onClick={()=>showHideSpecs("hide")}
					aria-label={productDtlsNLS.ShowLessARIAProductSpecificaties.t()}
				>
					{productDtlsNLS.ShowLess.t()}
				</Button>
			</Box>
		</Box>
	) : null;
};
const Attachments: FC<Props> = ({ attachments }) => {
		return <Attachment {...{ attachments }} />;
	}
export const ProductDetailsTabs: FC = () => {
	const localization = useLocalization('productDetail');
	const { selection, product } = useContext(ContentContext) as ReturnType<
		typeof useProductDetails
	> &
		ReturnType<typeof useSkuListTable>;
	const sku = !productIsA(product, TYPES.bundle) ? selection?.sku : product;
	const {
		tabsData: { longDescription, descriptiveAttributes, attachments, longCompDesc, shortDescription },
	} = getProductDisplayInfo(sku, product);

	const { isMobile } = useDeviceType();
	const attrs = product?.attributes;
	const manuFacturerAttr = findAttributeByIdentifier(attrs, 'Manufacturer number');
	const supplierAttr = findAttributeByIdentifier(attrs, 'EUResponsible');
	const {supplierData} = useSupplierData(product?.id as string);
	 
	const customAttrObject = useMemo(() => {
		if(! (manuFacturerAttr || supplierAttr)) return {}
		try{	
			if(supplierData !== undefined){
				const customAttrObject:Record<string,any> = {};
				const supplierDataTmp = supplierData?.supplier as string;
				const supplierRows = supplierDataTmp.split('~');
				supplierRows.forEach((row:string) =>{ 
					const columns = row.split("/_/");
					if(manuFacturerAttr?.values?.at(0)?.value === columns[0]){					
					customAttrObject["Gegevens fabrikant"]=createCustomAttributes("ManuFacturer",columns);
				} 
				if (supplierAttr?.values?.at(0)?.value === columns[0]){					
					customAttrObject["EU-verantwoordelijke"]=createCustomAttributes("Supplier",columns);
				} 
				}) 
				return customAttrObject??{} as Record<string,any>;
			}			
			
		}catch(e){}
		}, [supplierData]); 
 
	
	const tabs: TabData[] = [
		{
			title: localization.DescriptionHeading.t(),
			id:"tab_description",
			F: LongDesc,
			data: { longDescription, longCompDesc, shortDescription },
		},
		{
			title: localization.ProductDetails.t(),
			id:"tab_specifications",
			F: DescriptiveAttributes,
			data: descriptiveAttributes
		},
		{ title: localization.Attachments.t(), F: Attachments, data: attachments, id:"tab_attachments", },
	]
		.filter(({ data }) => !!(Array.isArray(data) ? data.length : data))
		.map((tab) => ({
			...tab,
			content: <tab.F {...{ longDescription, descriptiveAttributes, customAttrObject, attachments, longCompDesc, shortDescription }} />,
		}));
	
	const scrollToSection = useCallback((id: string) => {
		const target = document.getElementById(id);
		if (target) {
			target.setAttribute("tabindex", "-1");
			target.focus();
			const top = target.getBoundingClientRect().top + window.scrollY - (isMobile ? 8 : 24);
			window.scrollTo({
				top,
				behavior: 'smooth',
			});
		}
	},[isMobile]);

	return !isMobile ? <TabsDesktop {...{tabs, scrollToSection}} /> 
					: <TabsMobile {...{tabs, scrollToSection}} />
};
